import { useMemo, useState } from 'react';
import { Package, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { useItems } from '@/features/inventory/hooks';

interface WorkItemPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stationId: string | null;
  currentItemId: string | null;
  onPick: (itemId: string | null) => void;
  title?: string;
  allowNone?: boolean;
}

/** Pick what's being built. Items linked to this station float to the top. */
export function WorkItemPicker({ open, onOpenChange, stationId, currentItemId, onPick, title = 'What are you building?', allowNone = false }: WorkItemPickerProps) {
  const { data: items = [], isLoading } = useItems();
  const [q, setQ] = useState('');

  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = items.filter(
      (i) =>
        i.status !== 'archived' &&
        i.is_active !== false &&
        (!needle || i.name.toLowerCase().includes(needle) || i.sku.toLowerCase().includes(needle)),
    );
    const here = (i: (typeof items)[number]) => (stationId && i.workstations?.some((w) => w.id === stationId) ? 0 : 1);
    return filtered.sort((a, b) => here(a) - here(b) || a.name.localeCompare(b.name)).slice(0, 60);
  }, [items, q, stationId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-hidden rounded-2xl p-0">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Package className="h-5 w-5 text-info" />
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="px-6 pt-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or SKU" className="h-12 pl-9 text-base" autoFocus />
          </div>
        </div>
        <ul className="max-h-[52vh] overflow-y-auto px-6 py-4">
          {allowNone && (
            <li>
              <button
                type="button"
                onClick={() => {
                  onPick(null);
                  onOpenChange(false);
                }}
                className={cn('mb-2 flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left', currentItemId === null ? 'border-info bg-info-soft/30' : 'border-border hover:bg-secondary')}
              >
                <span className="text-sm font-medium">No specific item</span>
              </button>
            </li>
          )}
          {isLoading && <li className="py-6 text-center text-sm text-muted-foreground">Loading catalog…</li>}
          {!isLoading && list.length === 0 && <li className="py-6 text-center text-sm text-muted-foreground">Nothing matches.</li>}
          {list.map((i) => {
            const here = stationId ? i.workstations?.some((w) => w.id === stationId) : false;
            const active = i.id === currentItemId;
            return (
              <li key={i.id}>
                <button
                  type="button"
                  onClick={() => {
                    onPick(i.id);
                    onOpenChange(false);
                  }}
                  className={cn(
                    'mb-2 flex min-h-[56px] w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors',
                    active ? 'border-info bg-info-soft/30' : 'border-border bg-card hover:bg-secondary',
                  )}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{i.name}</p>
                    <p className="text-xs text-muted-foreground">{i.sku}</p>
                  </div>
                  {here && <span className="shrink-0 rounded-full bg-bright-moss/40 px-2 py-0.5 text-[11px] font-medium text-bright-moss-dark">This station</span>}
                </button>
              </li>
            );
          })}
        </ul>
        <div className="flex justify-end border-t border-border px-6 py-4">
          <Button variant="ghost" size="lg" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
